import i18next from "i18next";
import AwaitingCompilationIcon from "@mui/icons-material/HourglassEmpty";
import CompiledIcon from "@mui/icons-material/Addchart";
import CompilationConfirmedIcon from "@mui/icons-material/InsertChart";
import AwaitingEvaluationIcon from "@mui/icons-material/GppMaybe";
import EvaluationConfirmedIcon from "@mui/icons-material/CheckCircle";
import HelpIcon from "@mui/icons-material/Help";
import { OddsTooltip } from "../components/OddsTooltip";
import { getLeagueDisplayName, LeagueDisplay } from "../components/LeagueDisplay";
import { STATI } from "../helpers/stati";
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from "@mui/x-data-grid-pro";
import { IconButton } from "@mui/material";
import ExpandIcon from "@mui/icons-material/Expand";
import AsianHandicapMenu from "./AsianHandicapMenu";
import { fetchFromApi } from "../helpers/fetchFromApi";

function CustomDetailPanelToggle() {
  return (
    <IconButton aria-label="expand" title={i18next.t("help.tooltips.expandFixtureDetails")}>
      <ExpandIcon />
    </IconButton>
  );
}

async function getCountryRequest(country_id) {
  const response = await (
    await fetchFromApi(`fetch-country/${country_id}`)
  ).json();
  return response.data
}

export const fixtureColumns = [
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    headerName: "Expand",
    type: "string",
    field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
    renderCell: (params) => <CustomDetailPanelToggle {...params} />,
  },
  {
    headerName: "status",
    field: "status",
    flex: 0.5,
    renderCell: ({ value }) => {
      const map = {
        "awaiting compilation": AwaitingCompilationIcon,
        compiled: CompiledIcon,
        "compilation confirmed": CompilationConfirmedIcon,
        "awaiting evaluation": AwaitingEvaluationIcon,
        finalized: EvaluationConfirmedIcon,
      };
      const map2 = Object.keys(map).reduce(
        (prev, cur) => ({
          ...prev,
          [STATI[cur]]: { icon: map[cur], title: cur },
        }),
        {}
      );
      const { icon: Icon, title } = map2[value] ?? {
        icon: HelpIcon,
        title: "no status",
      };
      return <Icon titleAccess={title} />;
    },
  },
  {
    headerName: "kickoff time",
    field: "details",
    flex: 1,
    renderCell: ({value}) => {
      return (
      <div title={value.id}>
        <div>
          {value.date.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "numeric",
            month: "numeric",
          })}{" "}
          {value.date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    )},
  },
  {
    headerName: "competition",
    field: "league",
    flex: 1,
    renderCell: ({ value }) => {
      if (!getLeagueDisplayName(value)) {
        return null;
      }

      return <LeagueDisplay league={value} compact />;
    },
  },
  {
    headerName: "home team",
    field: "localTeam",
    flex: 1.5,
    renderCell: ({ value }) => {
      return (<div
        onMouseEnter={(event) => {
          getCountryRequest(value.country_id).then((response) => {
            event.target.title = response.official_name;
          })
        }}
        style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
        {<img src={value.image_path} style={{ height: "1.5em" }} alt="" />}
        {value.name}
      </div>
      )
    },
  },
  {
    headerName: "away team",
    field: "visitorTeam",
    flex: 1.5,
    renderCell: ({ value }) => (
      <div
        onMouseEnter={(event) => {
          getCountryRequest(value.country_id).then((response) => {
            event.target.title = response.official_name;
          })
        }}
        style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
        {<img src={value.image_path} style={{ height: "1.5em" }} alt="" />}
        {value.name}
      </div>
    ),
  },
  {
    headerName: "result",
    description: "Fixture result after game is finished. Could also be live result.",
    field: "scores",
    flex: "1 0 auto",
    renderCell: ({ value }) => {
      if (Object.keys(value).length === 0) {
        return (<span>-</span>);
      }
      if (value[0].finish === "POSTPONED" || value[0].finish === "CANCELLED") {
        return <span>-</span>;
      }
      var afterSecondTimeHome = getByTypeId(value, 2, "home");
      var afterSecondTimeAway = getByTypeId(value, 2, "away");

      var result = "-"
      if (afterSecondTimeHome !== "" && afterSecondTimeAway !== "") {
        result = afterSecondTimeHome + "-" + afterSecondTimeAway;
      }

      var afterFirstTimeHome = getByTypeId(value, 1, "home");
      var afterFirstTimeAway = getByTypeId(value, 1, "away");

      if (afterFirstTimeHome !== "" && afterFirstTimeAway !== "") {
        result += " (" + afterFirstTimeHome + "-" + afterFirstTimeAway + ")";
      }

      var afterExtraTimeHome = getByTypeId(value, 3, "home");
      var afterExtraTimeAway = getByTypeId(value, 3, "away");
      if (afterExtraTimeHome !== "" && afterExtraTimeAway !== "") {
        result += " (" + afterExtraTimeHome + "-" + afterExtraTimeAway + ")";
      }

      var afterPenaltyShootoutHome = getByTypeId(value, 5, "home");
      var afterPenaltyShootoutAway = getByTypeId(value, 5, "away");
      if (afterPenaltyShootoutHome !== "" && afterPenaltyShootoutAway !== "") {
        result += " (" + afterPenaltyShootoutHome + "-" + afterPenaltyShootoutAway + ")";
      }

      return <span>{result}</span>;
    },
  },
  {
    headerName: "true home",
    field: "homewin",
    description: "true price for home win. Reciprocal of win probability home side",
    flex: 1,
    renderCell: ({ value, row }) => (
      <OddsTooltip
        oddsId={row.id}
        label="1"
      >
        <span>{value ? (1 / value).toFixed(2) : "-"}</span>
      </OddsTooltip>
    ),
  },
  {
    headerName: "true draw",
    field: "draw",
    description: "true price for the draw. Reciprocal of draw probability",
    flex: 1,
    renderCell: ({ value, row }) => (
      <OddsTooltip
        oddsId={row.id}
        label="X"
      >
        <span>{value ? (1 / value).toFixed(2) : "-"}</span>
      </OddsTooltip>
    ),
  },
  {
    headerName: "true away",
    field: "awaywin",
    description: "true price for away win. Reciprocal of away win probability",
    flex: 1,
    renderCell: ({ value, row }) => (
      <OddsTooltip
        oddsId={row.id}
        label="2"
      >
        <span>{value ? (1 / value).toFixed(2) : "-"}</span>
      </OddsTooltip>
    ),
  },
  {
    headerName: "Extras",
    field: "agl",
    description: "See more for that fixture",
    flex: 1,
    renderCell: ({ row }) => <AsianHandicapMenu row={row} />,
  }
];
function getByTypeId(value, typeId, side) {
  const t = value.find((it) => it.type_id === typeId && it.score.participant === side);
  return t ? t.score.goals : "";
}
